import { TypeChecker } from '@attain-apps/type-guards'

import * as models from '../../models'

import { Inheritance } from './inheritance'

export interface Ref {
  /**
   * Reference attribute that links this subquery with the parent query. Both
   * the parent query and this subquery must have a target. This attribute must
   * exist on an entity that is either one of those targets or contained in one
   * of those targets (for targets that are dimensions). See the examples below
   * for more details
   *
   * @example
   * // TODO:
   */
  attr: models.Attribute.Id
  /**
   * Determines if references to ancestors and/or
   * descendants count as references to this entity
   *
   * For example, if we are looking at departments and we see this department
   * has a subdepartment that is assigned to a room, should we interpret that as
   * meaning this department is assigned to that room? If yes, inherit should be
   * set to {@link Inheritance.Descendants}
   *
   * @default Inheritance.None
   * @example
   * // TODO: move the above example down here and convert it into an actual query
   */
  inherit?: Inheritance
}
export namespace Ref {
  export const isRef: TypeChecker<Ref> = TypeChecker.Factory.lazy(() =>
    TypeChecker.Factory.object<Ref>({
      attr: models.Attribute.Id.isId,
      inherit: TypeChecker.Factory.optional(Inheritance.isInheritance),
    }),
  )

  export function stringify(ref: Readonly<Ref>): string {
    const { attr, inherit } = WithDefaults.from(ref)
    return JSON.stringify({ attr, inherit } satisfies Record<keyof Ref, string>)
  }

  export type WithDefaults = Required<Ref>
  export namespace WithDefaults {
    export function from(ref: Readonly<Ref>): WithDefaults
    export function from(
      ref: Readonly<Ref> | undefined,
    ): WithDefaults | undefined
    export function from(
      ref: Readonly<Ref> | undefined,
    ): WithDefaults | undefined {
      if (!ref) {
        return undefined
      }
      const { attr, inherit = '=' } = ref
      return { attr, inherit }
    }
  }
}
